import { AlertTriangle, CheckCircle, Info, XCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import { getTheme, type ThemeMode } from '../theme'

export type NotificationType = 'success' | 'error' | 'info' | 'warning'

export type NotificationOptions = {
  id?: string
  duration?: number
  position?:
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right'
  icon?: React.ReactElement | string
  style?: React.CSSProperties
  className?: string
  theme?: ThemeMode
}

// Get theme-aware styles
const getThemeStyles = (mode: ThemeMode = 'light'): React.CSSProperties => {
  const theme = getTheme(mode)
  
  return {
    background: theme.surface.elevated,
    color: theme.text.primary,
    borderRadius: '8px',
    padding: '12px 16px',
    fontSize: '14px',
    fontWeight: '500',
    boxShadow: theme.modal.shadow,
    border: `1px solid ${theme.border.primary}`,
  }
}

// Get icon configurations with theme-aware colors
const getIconConfig = (mode: ThemeMode = 'light') => {
  const theme = getTheme(mode)
  
  return {
    success: (
      <CheckCircle
        size={20}
        style={{ color: theme.status.success.icon }}
      />
    ),
    error: (
      <XCircle size={20} style={{ color: theme.status.error.icon }} />
    ),
    warning: (
      <AlertTriangle
        size={20}
        style={{ color: theme.status.warning.icon }}
      />
    ),
    info: <Info size={20} style={{ color: theme.status.info.icon }} />,
  }
}

// Default configuration (will be overridden with theme)
const getDefaultConfig = (mode: ThemeMode = 'light'): NotificationOptions => {
  return {
    duration: 3000,
    position: 'bottom-center',
    style: getThemeStyles(mode),
    theme: mode,
  }
}

/**
 * Get current theme mode from context or default to 'light'
 * This will be set by NotificationProvider
 */
let currentThemeMode: ThemeMode = 'light'

export const setThemeMode = (mode: ThemeMode) => {
  currentThemeMode = mode
}

/**
 * show a notification with theme-aware styling
 */
export const showNotification = (
  message: string,
  type: NotificationType = 'info',
  options: NotificationOptions = {},
) => {
  const themeMode = options.theme || currentThemeMode
  const defaultConfig = getDefaultConfig(themeMode)
  const iconConfig = getIconConfig(themeMode)
  
  const config = {
    ...defaultConfig,
    ...options,
    style: {
      ...defaultConfig.style,
      ...options.style,
    },
    icon: options.icon || iconConfig[type],
  }

  switch (type) {
    case 'success':
      return toast.success(message, config)
    case 'error':
      return toast.error(message, config)
    case 'warning':
      return toast(message, { ...config, icon: iconConfig.warning })
    case 'info':
    default:
      return toast(message, config)
  }
}

/**
 * show a success notification
 */
export const showToastSuccess = (
  message: string,
  options?: NotificationOptions,
) => {
  return showNotification(message, 'success', options)
}

/**
 * show an error notification
 */
export const showToastError = (
  message: string,
  options?: NotificationOptions,
) => {
  return showNotification(message, 'error', options)
}

/**
 * show a warning notification
 */
export const showToastWarning = (
  message: string,
  options?: NotificationOptions,
) => {
  return showNotification(message, 'warning', options)
}

/**
 * show an info notification
 */
export const showToastInfo = (
  message: string,
  options?: NotificationOptions,
) => {
  return showNotification(message, 'info', options)
}

/**
 * show a custom notification with specific styling
 */
export const showToastCustom = (
  message: string,
  options: NotificationOptions,
) => {
  const themeMode = options.theme || currentThemeMode
  const defaultConfig = getDefaultConfig(themeMode)
  
  return toast(message, {
    ...defaultConfig,
    ...options,
    style: {
      ...defaultConfig.style,
      ...options.style,
    },
  })
}

/**
 * show a notification with explicit theme support
 * @deprecated Use showNotification with options.theme instead
 */
export const showThemedNotification = (
  message: string,
  type: NotificationType = 'info',
  isDark: boolean = false,
  options: NotificationOptions = {},
) => {
  return showNotification(message, type, {
    ...options,
    theme: isDark ? 'dark' : 'light',
  })
}

/**
 * dismiss all notifications
 */
export const dismissAll = () => {
  toast.dismiss()
}

/**
 * dismiss a specific notification
 */
export const dismiss = (toastId: string) => {
  toast.dismiss(toastId)
}

/**
 * promise notification for async operations
 */
export const showPromise = <T,>(
  promise: Promise<T>,
  messages: {
    loading: string
    success: string | ((data: T) => string)
    error: string | ((error: any) => string)
  },
  options?: Omit<NotificationOptions, 'icon'>,
) => {
  const themeMode = options?.theme || currentThemeMode
  const defaultConfig = getDefaultConfig(themeMode)
  
  return toast.promise(
    promise,
    {
      loading: messages.loading,
      success: messages.success,
      error: messages.error,
    },
    {
      duration: options?.duration || defaultConfig.duration,
      position: options?.position || defaultConfig.position,
      style: {
        ...defaultConfig.style,
        ...options?.style,
      },
    },
  )
}
